import React from 'react';

import debounce from 'lodash/debounce';

import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { CONTENT_TYPES } from '@lumapps/content-types/keys';
import { ANCHOR_FROM_FO } from '@lumapps/contents/constants';
import { useDataAttributes } from '@lumapps/data-attributes';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { MenuContext } from '@lumapps/lumx-menu/components/Menu/context';
import { mdiTablet, mdiViewQuilt } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { Link as RouterLink } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { InfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';

import { useContentTemplatesQuery } from '../../hooks/useContentTemplatesQuery';
import { CONTENT_TEMPLATES } from '../../keys';
import { contentCreation, contentCreationCustomTemplate } from '../../routes';
import { FIELDS, MAX_RESULTS } from './constants';

export interface TemplateMenuProps {
    /** Id of the content type owning the templates. Retrieved from the store. */
    contentTypeId: string;
    /** whether the defaul template option is enabled for this type of template */
    defaultTemplateEnabled: boolean;
    /** whether content creation link should open in new tab */
    shouldOpenInNewTab?: boolean;
    /** whether we should open the writer editor instead of the classic one */
    shouldOpenWriterEditor?: boolean;
    /** scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
    /**
     * Whether this menu is used from a front-office context.
     * When true (default), the "create from scratch" link includes #ffo so the NCE
     * close button redirects back to the front-office. When false (back-office context),
     * no anchor is added so close returns to the back-office admin page instead.
     */
    fromFrontOffice?: boolean;
}

/**
 * Menu of all available content template to create content from.
 */
export const TemplateMenu: React.FC<TemplateMenuProps> = (props) => {
    const {
        contentTypeId,
        defaultTemplateEnabled,
        shouldOpenWriterEditor,
        scope,
        shouldOpenInNewTab,
        fromFrontOffice = true,
    } = props;
    const { get } = useDataAttributes(scope);
    const { translateKey, translateObject } = useTranslate();

    const lang = useSelector(currentLanguageSelector);
    const instance = useSelector(instanceIdSelector);

    const params = React.useMemo(
        () => ({
            fields: FIELDS,
            customContentType: contentTypeId,
            instance,
            lang,
            maxResults: MAX_RESULTS,
            showTemplatesWithInheritedOnlyParam: true,
        }),
        [contentTypeId, instance, lang],
    );

    const {
        items: templateList,
        hasNextPage,
        isInitialLoading,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
    } = useContentTemplatesQuery(params, { enabled: true, cacheTime: Infinity, staleTime: Infinity });

    const debouncedFetchNextPage = debounce(fetchNextPage, DEBOUNCE_DELAY);

    if (isInitialLoading) {
        // Wrap the skeleton in a MenuContext with isLoading:true so MenuItemSkeleton renders
        // as <li aria-hidden> instead of <MenuItem>. Without this, MenuItemSkeleton registers
        // in the roving tabindex; when it unmounts after loading, the deregistration corrupts
        // the blur-based close mechanism of the parent Menu.Trigger for the first open.
        return (
            <MenuContext.Consumer>
                {(ctx) => (
                    // eslint-disable-next-line react/jsx-no-constructed-context-values
                    <MenuContext.Provider value={ctx ? { ...ctx, isLoading: true } : ctx}>
                        <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />
                    </MenuContext.Provider>
                )}
            </MenuContext.Consumer>
        );
    }

    // When the user cannot create from scratch and no templates exist, show a disabled placeholder
    // so the dropdown is never empty and can still be dismissed.
    if (!defaultTemplateEnabled && templateList.length === 0) {
        return <Menu.Item isDisabled>{translateKey(CONTENT_TEMPLATES.EMPTY_STATE_TITLE)}</Menu.Item>;
    }

    return (
        <>
            {Object.values(templateList).map((template) => (
                <Menu.Item
                    key={template.id}
                    icon={mdiViewQuilt}
                    as={RouterLink}
                    target={shouldOpenInNewTab ? '_blank' : undefined}
                    to={contentCreationCustomTemplate(contentTypeId, template.id, {
                        shouldOpenWriterEditor,
                    })}
                    {...get({ element: contentTypeId, action: 'template', type: template.id })}
                >
                    {translateObject(template.name)}
                </Menu.Item>
            ))}

            {isFetchingNextPage && (
                <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />
            )}

            {defaultTemplateEnabled && templateList?.length > 0 && <Menu.Divider />}

            {defaultTemplateEnabled && (
                <Menu.Item
                    icon={mdiTablet}
                    as={RouterLink}
                    target={shouldOpenInNewTab ? '_blank' : undefined}
                    to={contentCreation(contentTypeId, { anchor: fromFrontOffice ? ANCHOR_FROM_FO : undefined })}
                    {...get({ element: contentTypeId, action: 'template', type: 'empty' })}
                >
                    {translateKey(CONTENT_TYPES.CREATE_FROM_SCRATCH)}
                </Menu.Item>
            )}

            {hasNextPage && !isLoading && <InfiniteScroll callback={() => debouncedFetchNextPage()} />}
        </>
    );
};
